import { ValidationResponse, ValidationStatuses } from './types';
import apiClient from 'apiClient';
import { AxiosResponse } from 'axios';
import cachedValidateRequest from './cachedValidateRequest';
import { ProductRepaymentType } from 'domain/Product';
import { Nullable } from 'utils/types';

interface Params {
  maxLtvAllowed: number;
  minLtvAllowed: number;
}
interface ResponseData extends Omit<ValidationResponse, 'params'> {}
interface ResponseDataWithParams extends Required<ValidationResponse<Params>> {}

export class LTVValidationError extends Error {
  params: Params;

  constructor(params: Params) {
    super(
      `The ${params.minLtvAllowed ? 'minimum' : 'maximum'} LTV we can consider is ${
        params.minLtvAllowed ? params.minLtvAllowed : params.maxLtvAllowed
      }%`,
    );

    this.params = params;
  }
}

const isResponseWithParams = (
  response: AxiosResponse<ValidationResponse<Params>>,
): response is AxiosResponse<ResponseDataWithParams> => response.data.status !== ValidationStatuses.OK;

const validate = async (
  productType: string,
  loanAmount: number,
  propertyValue: number,
  productRepaymentType: Nullable<ProductRepaymentType>,
): Promise<ResponseData> => {
  const response = await apiClient.post<ValidationResponse<Params>>('/api/v1/business-rules/validate-ltv', {
    productType,
    loanAmount,
    propertyValue,
    productRepaymentType,
  });

  if (isResponseWithParams(response)) {
    throw new LTVValidationError(response.data.params);
  }

  return { status: response.data.status, details: response.data.details };
};

const cachedValidate = async (
  productType: string,
  loanAmount: number,
  propertyValue: number,
  productRepaymentType: Nullable<ProductRepaymentType>,
) => {
  return cachedValidateRequest(async () => {
    return await validate(productType, loanAmount, propertyValue, productRepaymentType);
  }, `validate-lease-length-${productType}-${loanAmount}-${propertyValue}-${productRepaymentType}`);
};

const exportObject = { validate, cachedValidate };

export default exportObject;
